import React, { useEffect } from "react";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import { Box, Typography, Link } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { useFormContext } from "react-hook-form";
import { map, isEqual } from "lodash-es";
import usePersistedState from "../../hooks/usePersistedState";

import { Title } from "../Typography";
import Question from "./Question";

const useStyles = makeStyles((theme) => ({
  content: {
    width: "100%",
    margin: "auto",
    borderRadius: theme.shape.borderRadius,
    paddingBottom: theme.spacing(10),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  halfWidthQuestion: {
    width: `calc(50% - ${theme.spacing(0.5)}px)`,
    "&:nth-child(even)": {
      marginLeft: theme.spacing(0.5),
    },
    "&:nth-child(odd)": {
      marginRight: theme.spacing(0.5),
    },
  },
  link: {
    color: "inherit",
  },
}));

const isNameQuestion = (question) =>
  ["FIRST_NAME", "LAST_NAME"].includes(question.key);

const getQuestionsWithConditionalText = ({ questions, machineAnswers }) => {
  return questions.map((question) => {
    const { key } = question;

    if (
      key === "CASE_DESCRIPTION" &&
      machineAnswers.VIOLATION_TYPE === "ACCIDENT"
    ) {
      return {
        ...question,
        message: "Beschreibe uns kurz was passiert ist (optional)",
      };
    }

    return question;
  });
};

const ContactDetailsStep = ({
  message,
  questions,
  machineAnswers,
  onSubmit,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { watch, reset, formState } = useFormContext();
  const { isDirty } = formState;
  const values = watch(map(questions, "key"));

  const [getPersistedValue, setPersistedValue] =
    usePersistedState("CONTACT_DETAILS");

  const questionsWithConditionalText = getQuestionsWithConditionalText({
    questions,
    machineAnswers,
  });

  useEffect(() => {
    const persistedValue = getPersistedValue();
    const shouldReset = !isDirty && !isEqual(persistedValue, values);

    if (shouldReset) {
      reset({ ...persistedValue });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPersistedValue(values);
  }, [values, setPersistedValue]);

  return (
    <div>
      {!isDesktop && (
        <Box p={2}>
          <Title text={message} />
        </Box>
      )}

      <Box className={classes.content}>
        {isDesktop && <Title text={message} />}

        <Box display="flex" flexWrap="wrap">
          {questionsWithConditionalText.map((question) =>
            isNameQuestion(question) && isDesktop ? (
              <Box className={classes.halfWidthQuestion} key={question.key}>
                <Question question={question} onSubmit={onSubmit} />
              </Box>
            ) : (
              <Question
                key={question.key}
                question={question}
                onSubmit={onSubmit}
              />
            )
          )}

          <Typography variant="body2">
            Mit dem Absenden der Anfrage erklärst du dich mit unserer{" "}
            <Link
              href="https://www.gofreem.de/datenschutz"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
              underline="always"
            >
              Datenschutzerklärung
            </Link>{" "}
            und unseren{" "}
            <Link
              href="https://www.gofreem.de/nutzungsbedingungen/"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
              underline="always"
            >
              Nutzungsbedingungen
            </Link>{" "}
            einverstanden.
          </Typography>
        </Box>
      </Box>
    </div>
  );
};

export default ContactDetailsStep;
